'use client';


/** Imports */
import zxcvbn from 'zxcvbn';
import Link from 'next/link';
import Image from 'next/image';
import {isEmail} from 'validator';
import classNames from 'classnames';
import styles from './page.module.css';
import {useRouter} from 'next/navigation';
import ReCAPTCHA from 'react-google-recaptcha';
import Dialog from '@/components/Dialog/Dialog';
import Button from '@/components/Button/Button';
import {signIn, useSession} from 'next-auth/react';
import {parseLanguageCode} from '@/i18n/localeUtils';
import {useLocale, useTranslations} from 'next-intl';
import {createUserAction} from '@/actions/createUser';
import {ORGANIZATION_INVITE_QUERY_PARAM} from '@/modules/organizationInviteUtils';
import {generateNewEmailOtpAction, updatePasswordAction, verifyEmailOtpAction} from '@/actions/account';
import React, {type ChangeEvent, type FormEvent, type MouseEvent, useEffect, useRef, useState} from 'react';
import {
    MdVisibility,
    MdLockOutline,
    MdOutlineEmail,
    MdPersonOutline,
    MdVisibilityOff,
    MdOutlineWarning,
    MdOutlineVisibility,
    MdOutlineVisibilityOff,
} from 'react-icons/md';


/** Types */
type ResetPasswordDialogProps = {
    reference: React.RefObject<HTMLDialogElement | null>;
};
type BaseAuthFormProps = Readonly<{
    hasSocialError: boolean;
}>;
type SignUpProps = BaseAuthFormProps & Readonly<{
    usernamePlaceholder: string;
    organizationInviteNonce?: string;
}>;
type SignInProps = BaseAuthFormProps & Readonly<{
    organizationInviteNonce?: string;
}>;
type AuthErrorNoteProps = Readonly<{
    children: React.ReactNode;
}>;


/**
 * Renders a localized sign-up form and handles user registration, verification (reCAPTCHA), terms agreement, and subsequent sign-in and redirect for new accounts.
 *
 * The component validates email, username, and password strength, shows social sign-in options, displays localized error messages, and redirects authenticated users to the dashboard.
 *
 * @returns The SignUp component's rendered JSX element.
 */
export function SignUp(props: SignUpProps) {
    const {hasSocialError, usernamePlaceholder, organizationInviteNonce} = props;

    // Hooks
    const {data: session} = useSession();
    const locale = useLocale();
    const router = useRouter();
    const t = useTranslations('Auth');
    const tCommon = useTranslations('common');

    // Refs
    const recaptchaRef = useRef<ReCAPTCHA>(null);

    // State
    const [isSigningUp, setIsSigningUp] = useState<boolean>(false);
    const [email, setEmail] = useState<string>('');
    const [username, setUsername] = useState<string>('');
    const [password, setPassword] = useState<string>('');
    const [passwordVisible, setPasswordVisible] = useState<boolean>(false);
    const [captchaToken, setCaptchaToken] = useState<string | null>(null);
    const [termsChecked, setTermsChecked] = useState<boolean>(false);
    const [isFormDirty, setIsFormDirty] = useState<boolean>(false);
    const [hasError, setHasError] = useState<string | false>(false);

    // Memos
    const emailHasError = !isEmail(email);
    const usernameHasError = (username.length < 3);
    const passwordStrength = zxcvbn(password).score;
    const passwordHasError = (passwordStrength < 2);

    // Effects
    useEffect(() => {
        if (!session) return;
        router.replace(getDashboardCallbackUrl(locale, organizationInviteNonce));
    }, [locale, organizationInviteNonce, router, session]);

    /**
     * Handle sign-up form submission by validating the form, creating a new account, and signing in on success.
     *
     * Prevents the default event, marks the form as dirty, and aborts if validation fails, terms are unchecked, or CAPTCHA is missing.
     * Guards against duplicate submissions; on success invokes account creation with locale context and then signs in with the new credentials.
     * On failure sets the component error state. Always resets the ReCAPTCHA, clears the CAPTCHA token, and clears the signing-up state.
     *
     * @param event - The form submit event
     */
    async function handleSignUpSubmit(event: FormEvent<HTMLFormElement>) {
        try {

            // Update state
            event.preventDefault();
            setIsFormDirty(true);
            if (isSigningUp) return;
            if (
                emailHasError
                || usernameHasError
                || passwordHasError
                || !termsChecked
                || !captchaToken
            ) return;
            setIsSigningUp(true);

            // Create user
            const {success, error} = await createUserAction({
                accountType: 'EMAIL',
                email, username, password, captchaToken,
            }, parseLanguageCode(tCommon('locale')));
            if (!success) {
                setHasError(error || 'INTERNAL_ERROR');
                return;
            } else {
                setHasError(false);
            }
            const result = await signIn('credentials', {email, password, redirect: false});
            if (!result?.ok) {
                setHasError('INTERNAL_ERROR');
                return;
            }
            window.location.href = getDashboardCallbackUrl(locale, organizationInviteNonce);
        } catch (error) {
            console.error(error);
            window.alert(error);
        } finally {
            recaptchaRef.current?.reset();
            setCaptchaToken(null);
            setIsSigningUp(false);
        }
    }


    // Render
    return (<>
        <form noValidate onSubmit={handleSignUpSubmit} aria-labelledby="auth-form-title">
            <div className="box-body">

                {/* Error */}
                {hasError && (
                    <AuthErrorNote>
                        {t(`error.${hasError}`)}
                    </AuthErrorNote>
                )}

                {/* Social sign-in */}
                <SocialSignIn
                    hasError={hasSocialError}
                    organizationInviteNonce={organizationInviteNonce}
                />

                {/* Email */}
                <div className={classNames('input-wrapper', emailHasError && isFormDirty && '--error')}>
                    <label htmlFor="email">
                        {t('form.email')}
                    </label>
                    <div className="wrapper">
                        <div className="icon">
                            <MdOutlineEmail/>
                        </div>
                        <input
                            required
                            id="email" type="email" name="email"
                            value={email} placeholder="abc@mainsent.com" autoComplete="email"
                            onInput={(event) => setEmail((event.target as HTMLInputElement).value)}
                        />
                    </div>
                    {(emailHasError && isFormDirty) && (
                        <div className="error-message">
                            {t('form.error.email')}
                        </div>
                    )}
                </div>

                {/* Username */}
                <div className={classNames('input-wrapper', usernameHasError && isFormDirty && '--error')}>
                    <label htmlFor="username">
                        {t('form.username')}
                    </label>
                    <div className="wrapper">
                        <div className="icon">
                            <MdPersonOutline/>
                        </div>
                        <input
                            required
                            id="username"
                            type="text"
                            name="username"
                            value={username}
                            placeholder={usernamePlaceholder}
                            autoComplete="username"
                            onInput={(event) => setUsername((event.target as HTMLInputElement).value)}
                        />
                    </div>
                    {(usernameHasError && isFormDirty) && (
                        <div className="error-message">
                            {t('form.error.username')}
                        </div>
                    )}
                </div>

                {/* Password */}
                <div className={classNames('input-wrapper', passwordHasError && isFormDirty && '--error')}>
                    <label htmlFor="password">
                        {t('form.password')}
                    </label>
                    <div className="wrapper">
                        <div className="icon">
                            <MdLockOutline/>
                        </div>
                        <input
                            required
                            id="password"
                            type={
                                (passwordVisible)
                                    ? 'text'
                                    : 'password'
                            }
                            name="password"
                            value={password}
                            placeholder="***"
                            autoComplete="new-password"
                            onInput={(event) => setPassword((event.target as HTMLInputElement).value)}
                        />
                        <button
                            type="button"
                            className="icon --right"
                            onClick={() => setPasswordVisible(!passwordVisible)}
                            aria-label={
                                (passwordVisible)
                                    ? tCommon('ariaLabels.hidePassword')
                                    : tCommon('ariaLabels.showPassword')
                            }
                        >
                            {(passwordVisible)
                                ? <MdOutlineVisibilityOff/>
                                : <MdOutlineVisibility/>}
                        </button>
                    </div>
                    <div className="password-strength-indicator">
                        <div
                            className="inner"
                            style={{width: `${passwordStrength / 4 * 100}%`}}
                            data-strength={passwordStrength}
                        />
                        <div className="min-strength"/>
                    </div>
                    {(passwordHasError && isFormDirty) && (
                        <div className="error-message">
                            {t('form.error.password')}
                        </div>
                    )}
                </div>

                {/* ReCAPTCHA */}
                <div className={classNames(
                    'input-wrapper',
                    styles.recaptchaField,
                    !captchaToken && isFormDirty && '--error',
                )}>
                    <ReCAPTCHA
                        ref={recaptchaRef}
                        size="normal"
                        hl={locale}
                        sitekey="6LdLmhgUAAAAALrSTxS_camrNTZ13ckjBCZfscaW"
                        onChange={(token) => setCaptchaToken(token)}
                    />
                    {(!captchaToken && isFormDirty) && (
                        <div className="error-message">
                            {t('form.error.captcha')}
                        </div>
                    )}
                </div>

                {/* Terms of service */}
                <div className={classNames(
                    'input-wrapper',
                    styles.termsField,
                    !termsChecked && isFormDirty && '--error',
                )}>
                    <div className="checkbox-wrapper">
                        <input
                            required
                            id="terms" type="checkbox" name="Terms of service"
                            checked={termsChecked}
                            onChange={(event) => setTermsChecked(event.target.checked)}
                        />
                        <label htmlFor="terms">
                            {t.rich('form.terms', {
                                a: (chunks) => <Link href={`/${locale}/legals/terms-of-service`}>{chunks}</Link>,
                                b: (chunks) => <Link href={`/${locale}/legals/privacy-policy`}>{chunks}</Link>,
                                br: () => <br/>,
                            })}
                        </label>
                    </div>
                </div>
            </div>
            <div className="box-foot">
                <button
                    type="submit"
                    className={classNames('button', styles.submitButton, isSigningUp && '--disabled')}
                    disabled={isSigningUp}
                    aria-busy={isSigningUp}
                >
                    <span className={styles.submitLabel}>
                        {t('signUp')}
                    </span>
                    {isSigningUp && (
                        <span className={styles.loadingSpinner} aria-hidden="true"/>
                    )}
                </button>
                {isSigningUp && (
                    <div className={styles.loadingStatus} role="status" aria-live="polite">
                        {t('doingSignUp')}
                    </div>
                )}
            </div>
        </form>
    </>);
}

export function SignIn(props: SignInProps) {
    const {hasSocialError, organizationInviteNonce} = props;

    // Refs
    const resetPasswordDialogRef = useRef<HTMLDialogElement>(null);

    // Hooks
    const {data: session} = useSession();
    const locale = useLocale();
    const router = useRouter();
    const t = useTranslations('Auth');
    const tCommon = useTranslations('common');

    // State
    const [isSigningIn, setIsSigningIn] = useState<boolean>(false);
    const [email, setEmail] = useState<string>('');
    const [password, setPassword] = useState<string>('');
    const [passwordVisible, setPasswordVisible] = useState<boolean>(false);
    const [isFormDirty, setIsFormDirty] = useState<boolean>(false);
    const [hasError, setHasError] = useState<boolean>(false);

    // Memos
    const emailHasError = !isEmail(email);
    const passwordHasError = (password.length === 0);

    // Effects
    useEffect(() => {
        if (!session) return;
        router.replace(getDashboardCallbackUrl(locale, organizationInviteNonce));
    }, [locale, organizationInviteNonce, router, session]);

    // Event handlers
    async function handleSignInSubmit(event: FormEvent<HTMLFormElement>) {
        try {

            // Update state
            event.preventDefault();
            setIsFormDirty(true);
            if (isSigningIn) return;
            if (emailHasError || passwordHasError) return;
            setIsSigningIn(true);

            // Sign in
            const result = await signIn('credentials', {email, password, redirect: false});
            const hasError = !result?.ok;
            setHasError(hasError);
            if (hasError) return;
            window.location.href = getDashboardCallbackUrl(locale, organizationInviteNonce);
        } catch (error) {
            console.error(error);
            window.alert(error);
        } finally {
            setIsSigningIn(false);
        }
    }


    // Render
    return (<>

        {/* Reset password modal */}
        <ResetPasswordDialog reference={resetPasswordDialogRef}/>

        {/* Main content */}
        <form noValidate onSubmit={handleSignInSubmit} aria-labelledby="auth-form-title">
            <div className="box-body">

                {/* Error */}
                {hasError && (
                    <AuthErrorNote>
                        {t.rich(`error.general`, {
                            br: () => <br/>,
                        })}
                    </AuthErrorNote>
                )}

                {/* Social sign-in */}
                <SocialSignIn
                    hasError={hasSocialError}
                    organizationInviteNonce={organizationInviteNonce}
                />

                {/* Email */}
                <div className={classNames('input-wrapper', emailHasError && isFormDirty && '--error')}>
                    <label htmlFor="email">
                        {t('form.email')}
                    </label>
                    <div className="wrapper">
                        <div className="icon">
                            <MdOutlineEmail/>
                        </div>
                        <input
                            required
                            id="email" type="email" name="email"
                            value={email} placeholder="abc@mainsent.com" autoComplete="email"
                            onInput={(event) => setEmail((event.target as HTMLInputElement).value)}
                        />
                    </div>
                    {(emailHasError && isFormDirty) && (
                        <div className="error-message">
                            {t('form.error.email')}
                        </div>
                    )}
                </div>

                {/* Password */}
                <div className={classNames('input-wrapper', passwordHasError && isFormDirty && '--error')}>
                    <label htmlFor="password">
                        {t('form.password')}
                    </label>
                    <div className="wrapper">
                        <div className="icon">
                            <MdLockOutline/>
                        </div>
                        <input
                            required
                            id="password" type={(passwordVisible) ? 'text' : 'password'} name="password"
                            value={password} placeholder="***" autoComplete="current-password"
                            onInput={(event) => setPassword((event.target as HTMLInputElement).value)}
                        />
                        <button
                            type="button"
                            className="icon --right"
                            onClick={() => setPasswordVisible(!passwordVisible)}
                            aria-label={
                                (passwordVisible)
                                    ? tCommon('ariaLabels.hidePassword')
                                    : tCommon('ariaLabels.showPassword')
                            }
                        >
                            {(passwordVisible)
                                ? <MdOutlineVisibilityOff/>
                                : <MdOutlineVisibility/>}
                        </button>
                    </div>
                    {(passwordHasError && isFormDirty) && (
                        <div className="error-message">
                            {t('form.error.password2')}
                        </div>
                    )}

                    {/* Forgot password */}
                    <button
                        type="button"
                        className={styles.forgotPassword}
                        onClick={() => resetPasswordDialogRef.current?.showModal()}
                    >
                        {t('form.forgotPassword')}
                    </button>
                </div>
            </div>
            <div className="box-foot">
                <button
                    type="submit"
                    className={classNames('button', styles.submitButton, isSigningIn && '--disabled')}
                    disabled={isSigningIn}
                    aria-busy={isSigningIn}
                >
                    <span className={styles.submitLabel}>
                        {t('signIn')}
                    </span>
                    {(isSigningIn) && (
                        <span className={styles.loadingSpinner} aria-hidden="true"/>
                    )}
                </button>
                {(isSigningIn) && (
                    <div className={styles.loadingStatus} role="status" aria-live="polite">
                        {t('doingSignIn')}
                    </div>
                )}
            </div>
        </form>
    </>);
}

function SocialSignIn(props: Readonly<{
    hasError: boolean;
    organizationInviteNonce?: string;
}>) {
    const {hasError, organizationInviteNonce} = props;

    // Hooks
    const t = useTranslations('Auth');
    const tAria = useTranslations('common.ariaLabels');
    const locale = useLocale();

    // Event handlers
    async function handleGoogleSignInClick(event: MouseEvent<HTMLButtonElement>) {
        event.preventDefault();
        await signIn('google', {callbackUrl: getDashboardCallbackUrl(locale, organizationInviteNonce)});
    }


    // Render
    return (
        <div className={styles.socialSignin} aria-label={tAria('socialSignIn')}>

            {/* Error */}
            {(hasError) && (
                <AuthErrorNote>
                    {t.rich(`error.social`, {
                        br: () => <br/>,
                    })}
                </AuthErrorNote>
            )}

            {/* Google */}
            <button
                type="button"
                className={classNames(styles.socialButton, styles.socialButtonGoogle)}
                onClick={handleGoogleSignInClick}
                aria-label={tAria('signInWithGoogle')}
            >
                <div className={styles.icon}>
                    <Image
                        src="/images/social/google.svg"
                        alt=""
                        width={32} height={32}
                        unoptimized={true}
                    />
                </div>
                <div className={styles.label}>
                    Google
                </div>
            </button>

            {/* Or */}
            <div className={styles.orItem}>
                <div className={styles.label}>
                    {t('form.or')}
                </div>
            </div>
        </div>
    );
}

function AuthErrorNote(props: AuthErrorNoteProps) {
    const {children} = props;
    return (
        <div className={styles.authErrorNote} role="alert">
            <div className={styles.authErrorIcon} aria-hidden="true">
                <MdOutlineWarning/>
            </div>
            <div className={styles.authErrorMessage}>
                {children}
            </div>
        </div>
    );
}

function getDashboardCallbackUrl(locale: string, organizationInviteNonce?: string) {
    const url = `/${locale}/dashboard`;
    if (!organizationInviteNonce) return url;
    const params = new URLSearchParams();
    params.set(ORGANIZATION_INVITE_QUERY_PARAM, organizationInviteNonce);
    return `${url}?${params.toString()}`;
}

/**
 * Render a three-step modal dialog that guides a user through requesting a verification code, verifying their identity with a one-time code, and setting a new password.
 *
 * The dialog validates the email format before sending a verification code, accepts a 6-digit numeric OTP for verification, and requires a password with minimum strength before saving. It also exposes controls via the provided dialog reference so the parent can open or close the modal.
 *
 * @param props.reference - A ref to the underlying HTMLDialogElement used to control opening, closing, and internal reset of the dialog state from a parent component.
 * @returns The Dialog element containing the multi-step reset-password user interface and its action buttons.
 */
function ResetPasswordDialog(props: ResetPasswordDialogProps) {
    const {reference} = props;

    // Hooks
    const t = useTranslations('Auth');
    const tCommon = useTranslations('common');
    const tAria = useTranslations('common.ariaLabels');

    // State
    const [step, setStep] = useState(0);
    const [verificationCode, setVerificationCode] = useState('');
    const [resendTimeout, setResendTimeout] = useState(0);
    const [hasError, setHasError] = useState(false);
    const [email, setEmail] = useState('');
    const [passwordVisible, setPasswordVisible] = useState(false);
    const [password, setPassword] = useState('');
    const [systemOTP, setSystemOTP] = useState('');

    // Memos
    const stepTitle = (() => {
        switch (step) {
            case 0:
                return t('resetPasswordDialog.setTitle1');
            case 1:
                return t('resetPasswordDialog.setTitle2');
            case 2:
                return t('resetPasswordDialog.setTitle3');
            default:
                return '';
        }
    })();
    const passwordStrength = zxcvbn(password).score;
    const passwordIsError = (passwordStrength < 2);

    // Event handlers
    function onVerificationCodeInputChange(event: ChangeEvent<HTMLInputElement>) {
        if (!event.target.value.match(/^\d{0,6}$/)) return;
        setVerificationCode(event.target.value);
    }

    async function onVerifyIdentityButtonClick() {
        if (!verificationCode) return;
        const result = await verifyEmailOtpAction(email, verificationCode);
        if (!result?.success || !result?.data) {
            setHasError(true);
            return;
        }
        setSystemOTP(result.data);
        setHasError(false);
        setVerificationCode('');
        setStep(2);
    }

    async function onSaveButtonClick() {
        if (passwordStrength < 2) return;
        const result = await updatePasswordAction(systemOTP, password);
        if (!result?.success) {
            setHasError(true);
            return;
        }
        reference.current?.close();
    }

    function onTogglePasswordVisibility() {
        setPasswordVisible(!passwordVisible);
    }

    function onEmailInputChange(event: ChangeEvent<HTMLInputElement>) {
        setEmail(event.target.value);
    }

    function resetDialogState() {
        setStep(0);
        setEmail('');
        setPassword('');
        setVerificationCode('');
        setResendTimeout(0);
        setHasError(false);
        setSystemOTP('');
        setPasswordVisible(false);
    }

    async function onSendVerificationCodeButtonClick() {
        if (!isEmail(email)) {
            setHasError(true);
            return;
        }
        setResendTimeout(20);
        const result = await generateNewEmailOtpAction(email, parseLanguageCode(tCommon('locale')));
        if (!result) {
            setHasError(true);
            return;
        }
        setHasError(false);
        setVerificationCode('');
        setStep(1);
    }


    // Effects
    useEffect(() => {
        if (resendTimeout <= 0) return;
        const timeout = window.setTimeout(() => {
            setResendTimeout((prev) => Math.max(0, prev - 1));
        }, 1_000);
        return () => window.clearTimeout(timeout);
    }, [resendTimeout]);


    // Render
    return (
        <Dialog
            reference={reference}
            headline={t('resetPasswordDialog.headline')}
            subHeadline={t('resetPasswordDialog.subHeadline')}
            disableCloseOnBackdrop={true}
            extraClasses={[styles.changeSecuredDataModal]}
            onOpen={resetDialogState}
            onClose={resetDialogState}
            foot={(<>

                {/* Step 1 */}
                {(step === 0) && (
                    <div className="flex-group">
                        <Button variant="primary" onClick={onSendVerificationCodeButtonClick}>
                            {t('resetPasswordDialog.buttonStep1')}
                        </Button>
                        {(hasError) && (
                            <div className="error-message">
                                {t('resetPasswordDialog.error.step1')}
                            </div>
                        )}
                    </div>
                )}

                {/* Step 2 */}
                {(step === 1) && (
                    <div className="flex-group">
                        <Button variant="primary" onClick={onVerifyIdentityButtonClick}>
                            {t('resetPasswordDialog.buttonStep2')}
                        </Button>
                        {(hasError) && (
                            <div className="error-message">
                                {t('resetPasswordDialog.error.step2')}
                            </div>
                        )}
                    </div>
                )}

                {/* Step 3 */}
                {(step === 2) && (
                    <div className="flex-group">
                        <Button variant="primary" onClick={onSaveButtonClick}>
                            {t('resetPasswordDialog.buttonStep3')}
                        </Button>
                        {(hasError) && (
                            <div className="error-message">
                                {t('resetPasswordDialog.error.step2')}
                            </div>
                        )}
                    </div>
                )}
            </>)}
        >
            <div className={styles.changeSecuredDataModal}>

                {/* Progress */}
                <div className={styles.progress}>
                    <div className={styles.row}>
                        <div className={styles.stepDot} data-active={step >= 0}/>
                        <div className={styles.stepLine} data-active={step >= 1}/>
                        <div className={styles.stepDot} data-active={step >= 1}/>
                        <div className={styles.stepLine} data-active={step >= 2}/>
                        <div className={styles.stepDot} data-active={step >= 2}/>
                    </div>
                    <div className={styles.row}>
                        <div className={styles.title}>
                            {stepTitle}
                        </div>
                    </div>
                </div>

                {/* Step 1 */}
                {(step === 0) && (
                    <div className={styles.step}>
                        <div className={styles.box}>

                            {/* Intro */}
                            <div className={styles.intro}>
                                {t('resetPasswordDialog.introStep1')}
                                <br/>
                                <span className={styles.small}>
                                    {t('resetPasswordDialog.introSmallStep1')}
                                </span>
                            </div>

                            {/* Input */}
                            <div className={styles.input}>
                                <input
                                    type="email"
                                    aria-label={t('form.email')}
                                    placeholder="abc@mainsent.com"
                                    value={email}
                                    onInput={onEmailInputChange}
                                />
                            </div>
                        </div>
                    </div>
                )}

                {/* Step 2 */}
                {(step === 1) && (
                    <div className={styles.step}>
                        <div className={styles.box}>

                            {/* Intro */}
                            <div className={styles.intro}>
                                {t.rich('resetPasswordDialog.introStep2', {
                                    email,
                                    br: () => <br/>,
                                    strong: (chunks) => <strong>{chunks}</strong>,
                                })}
                                <br/>
                                <span className={styles.small}>
                                    {t('resetPasswordDialog.introSmallStep2')}
                                </span>
                            </div>

                            {/* Input */}
                            <div className={styles.input}>
                                <input
                                    className="--otp"
                                    type="text"
                                    aria-label={t('resetPasswordDialog.placeholderVerificationCode')}
                                    placeholder={t('resetPasswordDialog.placeholderVerificationCode')}
                                    value={verificationCode}
                                    onInput={onVerificationCodeInputChange}
                                />
                            </div>

                            {/* Resend */}
                            <div className={styles.resend}>
                                {(resendTimeout > 0)
                                    ? t.rich('resetPasswordDialog.resendCodeWait', {
                                        seconds: resendTimeout,
                                        strong: (chunks) => <strong>{chunks}</strong>,
                                    })
                                    : t.rich('resetPasswordDialog.resendCodeAllowed', {
                                        button: (chunks) => (<>&nbsp;
                                            <Button variant="primary" onClick={onSendVerificationCodeButtonClick}>
                                                {chunks}
                                            </Button>
                                        </>),
                                    })}
                            </div>
                        </div>
                    </div>
                )}

                {/* Step 3 */}
                {(step === 2) && (
                    <div className={styles.step}>
                        <div className={styles.box}>

                            {/* Intro */}
                            <div className={styles.intro}>
                                {t('resetPasswordDialog.introStep3')}
                            </div>

                            {/* Input */}
                            <div className={styles.input}>
                                <input
                                    required
                                    className="password"
                                    aria-label={t('form.password')}
                                    placeholder="***"
                                    type={(passwordVisible) ? 'text' : 'password'} name="password" id="password"
                                    onChange={(event) => setPassword(event.target.value)}
                                />
                                <button
                                    type="button"
                                    className={styles.passwordVisibilityToggle}
                                    onClick={onTogglePasswordVisibility}
                                    aria-label={
                                        (passwordVisible)
                                            ? tAria('hidePassword')
                                            : tAria('showPassword')
                                    }
                                >
                                    {(passwordVisible)
                                        ? <MdVisibilityOff/>
                                        : <MdVisibility/>}
                                </button>
                            </div>
                            <div className={styles.passwordStrengthIndicator}>
                                <div
                                    className={styles.inner}
                                    style={{width: `${passwordStrength / 4 * 100}%`}}
                                    data-strength={passwordStrength}
                                />
                                <div className={styles.minStrength}/>
                            </div>
                            {(passwordIsError) && (
                                <div className="error-message">
                                    {t('form.error.password')}
                                </div>
                            )}
                        </div>
                    </div>
                )}
            </div>
        </Dialog>
    );
}
